import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, importProvidersFrom } from '@angular/core';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, withComponentInputBinding } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import {
  AnomalyEffects,
  CallEffects,
  CampaignEffects,
  CharityEffects,
  CustomItemEffects,
  DonationEffects,
  ItemEffects,
  JourneysEffects,
  MarketEffects,
  NotificationsEffects,
  OrganizationEffects,
  PageEffects,
  PartnerEffects,
  PhoneConfigurationEffects,
  provideCoreProviders,
  reducerToken,
  ScheduleHistoryEffects,
  StandardDonationEffects,
  StoreEffects,
  TrpEffects,
  TruckEffects,
  UsersEffects,
  XmileEffects,
  ZoneEffects,
} from '@core';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { LocalStorageService } from '@rspl-api';
import { AuthenticationService, authRoutes, provideRsplAuthProviders } from '@rspl-auth';
import { provideRsplUIProviders } from '@rspl-ui';
import { provideRsplVersion } from '@rspl-version';
import { LaddaModule } from 'angular2-ladda';
import { NgxStripeModule } from 'ngx-stripe';
import { firstValueFrom } from 'rxjs';

import { SurveyEffects } from './../../../libs/core/src/lib/store/survey/survey.effects';
import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideCoreProviders(environment),
    provideRsplUIProviders(),
    provideRsplVersion({ currentHash: '{{POST_BUILD_ENTERS_HASH_HERE}}' }),
    provideRouter(localStorage.getItem('rspl-user') ? appRoutes(environment) : authRoutes, withComponentInputBinding()),
    provideRsplAuthProviders(),
    importProvidersFrom(
      BrowserModule,
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: environment.production,
      }),
      NgxStripeModule.forRoot(environment.stripeKey),
      EffectsModule.forRoot([
        PageEffects,
        ZoneEffects,
        DonationEffects,
        CharityEffects,
        UsersEffects,
        StoreEffects,
        PartnerEffects,
        TruckEffects,
        MarketEffects,
        XmileEffects,
        TrpEffects,
        ScheduleHistoryEffects,
        StandardDonationEffects,
        NotificationsEffects,
        JourneysEffects,
        OrganizationEffects,
        PhoneConfigurationEffects,
        ItemEffects,
        CustomItemEffects,
        AnomalyEffects,
        CampaignEffects,
        CallEffects,
        SurveyEffects,
      ]),
      StoreModule.forRoot(reducerToken),
      StoreDevtoolsModule.instrument({
        maxAge: 25, // Retains last 25 states
        logOnly: environment.production, // Restrict extension to log-only mode
        connectInZone: true,
      }),
      LaddaModule.forRoot({
        style: 'zoom-in',
      }),
    ),
    provideAnimations(),
  ],
})
  .then((appRef) => {
    const authService = appRef.injector.get(AuthenticationService);
    const localStorageService = appRef.injector.get(LocalStorageService);
    const router = appRef.injector.get(Router);

    // Captain is in twilio flex iframe disable lead creation
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('flexIframe')) {
      window.sessionStorage.setItem('flexIframe', 'true');
    }

    // Listen for twilio flex navigation events
    window.addEventListener('message', (event) => {
      if (event?.data?.navigateTo) {
        router.navigateByUrl(event?.data?.navigateTo);
      }
    });

    firstValueFrom(authService.isLoggedIn$).then((isLoggedIn) => {
      const routes = isLoggedIn ? appRoutes(environment) : authRoutes;
      router.resetConfig(routes);
    });

    authService.onLogout$.subscribe(() => {
      window.parent.postMessage({ type: 'logout' }, '*');
    });

    authService.isLoggedIn$.subscribe((isLoggedIn) => {
      // Send user data to twilio flex
      const userData = localStorageService.getItem('rspl-user');
      if (userData) {
        const userInfo = JSON.parse(userData);
        delete userInfo.authorization;
        delete userInfo.meta;
        window.parent.postMessage({ userInfo }, '*');
      } else {
        window.parent.postMessage({ userInfo: null }, '*');
      }

      const newRoutes = isLoggedIn ? appRoutes(environment) : authRoutes;
      router.resetConfig(newRoutes);
    });
  })
  .catch((err) => console.error(err));
